/**
 * The scanner's second attempt (Android till only, today the C20 Pro): when the barcode scanner read nothing, the cashier photographs the rewards card.
 * The app takes the photo (window.AndroidPOS.capturePhoto -> window.onCardPhoto(base64 jpeg) / window.onCardPhotoError(message) /
 * window.onCardPhotoCancelled()), the server asks Claude to read the 10-digit membership number off it (POST /v3/membership/read-card, which only returns a
 * number with a valid check digit), and the number is then handled exactly like a scan of the card (MEM:<number>: lookup, attach to the order).
 *
 * This file is only the phone side (no network), so the scan button can use it without loading the API layer. The server read and the hand-off to the
 * normal lookup are in cardPhoto.js.
 *
 * Everything is guarded: in a browser, on the handheld and on iOS (no capturePhoto) the photo step simply does not exist.
 */

const bridge = () => (typeof window !== 'undefined' ? window.AndroidPOS : undefined);

/** True only on an app whose Android wrapper can take a photo for us and whose device has a camera (isCardPhotoAvailable, when the app has it). */
export function cardPhotoReady() {
  try {
    const b = bridge();
    if (!b || typeof b.capturePhoto !== 'function') return false;
    return typeof b.isCardPhotoAvailable === 'function' ? !!b.isCardPhotoAvailable() : true;
  } catch (e) {
    return false;
  }
}

export class CardPhotoCancelled extends Error {
  constructor() {
    super('cancelled');
    this.cancelled = true;
  }
}

/** Opens the camera in the app and resolves with the photo as base64 JPEG text. Rejects with CardPhotoCancelled, or an Error with a plain message. */
export function captureCardPhoto({ timeoutMs = 120000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!cardPhotoReady()) {
      reject(new Error('This device cannot take a photo of the card'));
      return;
    }
    let timer = null;
    const done = (fn, value) => {
      window.clearTimeout(timer);
      delete window.onCardPhoto;
      delete window.onCardPhotoError;
      delete window.onCardPhotoCancelled;
      fn(value);
    };
    window.onCardPhoto = (b64) => (typeof b64 === 'string' && b64 ? done(resolve, b64) : done(reject, new Error('The camera gave no photo')));
    window.onCardPhotoError = (message) => done(reject, new Error(typeof message === 'string' && message ? message : 'The camera could not take the photo'));
    window.onCardPhotoCancelled = () => done(reject, new CardPhotoCancelled());
    timer = window.setTimeout(() => done(reject, new Error('The camera took too long')), timeoutMs);
    try {
      bridge().capturePhoto();
    } catch (e) {
      done(reject, new Error('The camera could not be opened'));
    }
  });
}

